import React from "react";
import { Table, Empty, Tag, Switch, Input, Select, Button } from "antd";
import { SearchOutlined, DownloadOutlined } from "@ant-design/icons";
import "./style.scss";
import { serialNum } from "../../../utils";

const PINCODE_MAX_LENGTH = 6;

// Column widths kept in one place instead of scattered magic numbers.
const COLUMN_WIDTH = {
  serial: 80,
  pincode: 120,
  city: 120,
  state: 120,
  tag: 160,
  toggle: 110,
};

const SERVICE_TYPE_OPTIONS = [
  { label: "VayuQuick", value: "vayuquick" },
  { label: "VayuQuick Pro", value: "vayuquickPro" },
];

const YES_NO_OPTIONS = [
  { label: "Yes", value: "true" },
  { label: "No", value: "false" },
];

function ServiceabilityListTable({
  loading,
  tableData,
  pageSize,
  totalCount,
  currentPage,
  filters,
  handleTableChange,
  handleBookingOnlyToggle,
  handleActiveToggle,
  handleClearFilters,
  handleDownload,
  downloadLoading,
}) {
  const itemRender = (_, type, originalElement) => {
    if (type === "prev") {
      return <a>Previous</a>;
    }
    if (type === "next") {
      return <a>Next</a>;
    }
    return (
      <div>
        <p>{originalElement} </p>
      </div>
    );
  };

  const renderServiceTypeTags = (data) => {
    return (
      <div className="service-tags-container">
        {data.vayuquick && (
          <Tag className="service-tag vayuquick-tag">VayuQuick</Tag>
        )}

        {data.vayuquickPro && (
          <Tag className="service-tag vayuquick-pro-tag">VayuQuick Pro</Tag>
        )}

        {!data.vayuquick && !data.vayuquickPro && (
          <span className="no-tag">None</span>
        )}
      </div>
    );
  };

  // Shared Search/Reset action row for the filter dropdowns. `loading` mirrors the
  // table's fetch state so the Search button gives visual feedback while data loads.
  const renderFilterActions = (confirm, clearFilters, setSelectedKeys) => (
    <div className="vq-filter-actions">
      <Button
        type="primary"
        onClick={() => confirm()}
        icon={<SearchOutlined />}
        size="small"
        loading={loading}
      >
        Search
      </Button>
      <Button
        onClick={() => {
          clearFilters?.();
          setSelectedKeys([]);
          confirm();
        }}
        size="small"
      >
        Reset
      </Button>
    </div>
  );

  const filterIcon = (filtered) => (
    <SearchOutlined className={filtered ? "vq-filter-icon-active" : ""} />
  );

  // Filtering is server-side: the actual fetch is triggered on confirm() (Search
  // button / Enter) via the Table's onChange, NOT on each keystroke. onFilter must
  // still return true so AntD doesn't additionally filter the already-filtered rows
  // on the client (same pattern as the Pincode Management screen).
  const getTextColumnSearchProps = (columnKey, placeholder, numeric = false) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div className="vq-filter-dropdown" onKeyDown={(e) => e.stopPropagation()}>
        <Input
          className="vq-filter-control"
          placeholder={placeholder}
          value={selectedKeys[0]}
          allowClear
          maxLength={numeric ? PINCODE_MAX_LENGTH : undefined}
          onChange={(e) => {
            const val = numeric ? e.target.value.replace(/\D/g, "") : e.target.value;
            setSelectedKeys(val ? [val] : []);
          }}
          onPressEnter={() => confirm()}
        />
        {renderFilterActions(confirm, clearFilters, setSelectedKeys)}
      </div>
    ),
    filterIcon,
    filteredValue: filters?.[columnKey] ? [filters[columnKey]] : null,
    onFilter: () => true,
  });

  const getSelectColumnProps = (columnKey, placeholder, options) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div className="vq-filter-dropdown" onKeyDown={(e) => e.stopPropagation()}>
        <Select
          className="vq-filter-control"
          placeholder={placeholder}
          value={selectedKeys[0]}
          allowClear
          onChange={(value) => setSelectedKeys(value ? [value] : [])}
          options={options}
        />
        {renderFilterActions(confirm, clearFilters, setSelectedKeys)}
      </div>
    ),
    filterIcon,
    filteredValue:
      filters?.[columnKey] !== undefined &&
      filters?.[columnKey] !== null &&
      filters?.[columnKey] !== ""
        ? [filters[columnKey]]
        : null,
    filterMultiple: false,
    onFilter: () => true,
  });

  const columns = [
    {
      title: "Sl No",
      align: "center",
      key: "slNo",
      dataIndex: "slNo",
      width: COLUMN_WIDTH.serial,
      render: (a, b, idx) => serialNum(pageSize, currentPage, idx),
    },
    {
      title: "From Pincode",
      dataIndex: "fromPincode",
      key: "fromPincode",
      width: COLUMN_WIDTH.pincode,
      ...getTextColumnSearchProps("fromPincode", "Search From Pincode", true),
    },
    {
      title: "From City",
      dataIndex: "fromCity",
      key: "fromCity",
      width: COLUMN_WIDTH.city,
      ...getTextColumnSearchProps("fromCity", "Search From City"),
    },
    {
      title: "From State",
      dataIndex: "fromState",
      key: "fromState",
      width: COLUMN_WIDTH.state,
      ...getTextColumnSearchProps("fromState", "Search From State"),
    },
    {
      title: "To Pincode",
      dataIndex: "toPincode",
      key: "toPincode",
      width: COLUMN_WIDTH.pincode,
      ...getTextColumnSearchProps("toPincode", "Search To Pincode", true),
    },
    {
      title: "To City",
      dataIndex: "toCity",
      key: "toCity",
      width: COLUMN_WIDTH.city,
      ...getTextColumnSearchProps("toCity", "Search To City"),
    },
    {
      title: "To State",
      dataIndex: "toState",
      key: "toState",
      width: COLUMN_WIDTH.state,
      ...getTextColumnSearchProps("toState", "Search To State"),
    },
    {
      title: "Tag",
      dataIndex: "tag",
      key: "serviceType",
      width: COLUMN_WIDTH.tag,
      render: (_, record) => renderServiceTypeTags(record),
      ...getSelectColumnProps("serviceType", "Service Type", SERVICE_TYPE_OPTIONS),
    },
    {
      title: "Booking Only",
      dataIndex: "isBookingOnly",
      key: "isBookingOnly",
      width: COLUMN_WIDTH.toggle,
      align: "center",
      ...getSelectColumnProps("isBookingOnly", "Booking Only", YES_NO_OPTIONS),
      render: (value, record) => (
        <Switch
          checked={value}
          onChange={(checked) => handleBookingOnlyToggle(checked, record)}
          size="small"
        />
      ),
    },
    {
      title: "VQ Enabled",
      dataIndex: "isActive",
      key: "isActive",
      width: COLUMN_WIDTH.toggle,
      align: "center",
      ...getSelectColumnProps("isActive", "VQ Enabled", YES_NO_OPTIONS),
      render: (value, record) => (
        <Switch
          checked={value}
          onChange={(checked) => handleActiveToggle(checked, record)}
          size="small"
          loading={loading}
        />
      ),
    },
  ];

  const hasActiveFilters =
    filters && Object.values(filters).some((v) => v !== undefined && v !== null && v !== "");

  return (
    <div className="serviceability-table-container">
      <div className="table-header">
        <h3>Existing Serviceability Routes</h3>
        <div className="table-header-actions">
          <Button onClick={handleClearFilters} disabled={!hasActiveFilters}>
            Clear Filters
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            loading={downloadLoading}
            onClick={handleDownload}
          >
            Export
          </Button>
        </div>
      </div>
      <Table
        className="serviceability-table"
        columns={columns}
        dataSource={tableData}
        onChange={handleTableChange}
        loading={loading}
        defaultCurrent={1}
        pagination={{
          pageSize: pageSize,
          total: totalCount,
          current: currentPage,
          showSizeChanger: true,
          itemRender: itemRender,
        }}
        scroll={{ x: 1200, y: 400 }}
        size="middle"
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No serviceability data"
            />
          ),
        }}
      />
    </div>
  );
}

export default ServiceabilityListTable;
